import { Layers } from "lucide-react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { EmptyState } from "@/components/blocks/EmptyState";
import { FormRow, FormSection } from "@/components/blocks/FormRows";
import { StatGrid } from "@/components/blocks/StatGrid";
import { TableCard } from "@/components/blocks/TableCard";
import { Badge } from "@/components/catalyst/badge";
import { Input } from "@/components/catalyst/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table";
import { Button } from "@/components/ui/button";

const code = `import { StatGrid } from "@/components/blocks/StatGrid";
import { TableCard } from "@/components/blocks/TableCard";
import { EmptyState } from "@/components/blocks/EmptyState";
import { FormSection, FormRow } from "@/components/blocks/FormRows";
import { Table, TableBody, TableCell, TableRow } from "@/components/catalyst/table";

<StatGrid stats={[{ name: "Cards due", value: "12", change: "+2", trend: "up" }]} />

{/* Rows open through a link stretched over the row. */}
<TableCard>
  <Table bleed rowLinks className="[--gutter:--spacing(5)]">
    …
    <TableRow>
      <TableCell>
        <Link href="/decks/1" className="after:absolute after:inset-0">Deck title</Link>
      </TableCell>
    </TableRow>
  </Table>
</TableCard>

<EmptyState icon={Layers} title="No decks yet" action={<Button>New deck</Button>} />

<FormSection title="Profile" description="…">
  <FormRow label="Name" htmlFor="name"><Input id="name" /></FormRow>
</FormSection>`;

export function BlocksSection() {
  return (
    <SectionShell
      id="blocks"
      title="Blocks"
      description={
        <>
          Page-level pieces adapted from Tailwind's Application UI blocks, in{" "}
          <code>components/blocks/</code>: <code>StatGrid</code>,{" "}
          <code>TableCard</code>, <code>EmptyState</code>,{" "}
          <code>FormSection</code>/<code>FormRow</code>, <code>PageHeading</code>{" "}
          and <code>Tabs</code>. Tables, avatars and the sidebar come from the
          Catalyst components in <code>components/catalyst/</code>. Both follow
          the color tokens, so the accent color applies.
        </>
      }
      whenToUse={
        <ul>
          <li>Lists of records: a table card, not a hand-built list.</li>
          <li>Figures at the top of a page: a stat grid.</li>
          <li>An empty list: an empty state with a next step.</li>
          <li>Settings and profile forms: labels on the left.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Dense editors (cards, questions): keep their purpose-built layouts.</li>
          <li>Anything one of these already covers — don't rebuild it with raw utilities.</li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <StatGrid
            stats={[
              { name: "Cards due", value: "12", change: "+2", trend: "up" },
              { name: "Reviewed", value: "86", change: "last 7 days", trend: "flat" },
              { name: "Best score", value: "74%", change: "-3 pts", trend: "down" },
              { name: "Streak", value: "5", change: "days", trend: "flat" },
            ]}
          />
          <TableCard>
            <Table bleed className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
              <TableHead>
                <TableRow>
                  <TableHeader>Deck</TableHeader>
                  <TableHeader className="text-right">Status</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Clinical Foundations</TableCell>
                  <TableCell className="text-right">
                    <Badge color="accent">Published</Badge>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableCard>
          <EmptyState
            icon={Layers}
            title="No decks yet"
            description="Create a deck to start adding your own cards."
            action={<Button>New deck</Button>}
          />
          <FormSection title="Profile" description="Labels sit on the left at sm and up.">
            <FormRow label="Name" htmlFor="blocks-name" hint="Optional.">
              <Input id="blocks-name" name="name" className="sm:max-w-md" />
            </FormRow>
          </FormSection>
        </div>
      }
      code={code}
    />
  );
}
